import { AfterViewInit, ChangeDetectorRef, Component, ComponentRef, ElementRef, Input, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { AppModesEnum } from 'src/app/models/WebServer/appModesEnum';
import { PartecipantInfo } from 'src/app/models/WebServer/partecipantInfo';
import { PartecipantStatus, partToStr } from 'src/app/models/WebServer/partecipantStatus';
import { PhoneStatusEnum } from 'src/app/models/phoneStatus';
import { AppConfigService } from 'src/app/services/app.config.service';
import { I18Service } from 'src/app/services/i18.service';
import { MediaStremService } from 'src/app/services/media-stream.service';
import { WhiteboardService } from 'src/app/services/whiteboard.service';
import * as appSelector from 'src/app/store/selectors/app.selectors';
import * as sessionSelector from 'src/app/store/selectors/session.selectors';
import { AppState } from 'src/app/store/states/app.state';
import { ThumbVideoComponent } from './thumb-video.component';

@Component({
  selector: 'app-video',
  templateUrl: './video.component.html',
  styleUrls: ['./video.component.scss']
})
export class VideoComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() AS!: AppState;
  @Input() MYSTATUS!: PartecipantStatus;
  @Input() OTHERSTATUS?: PartecipantStatus;

  @ViewChild("videoContainer") public videoContainer!: ElementRef;

  private destroy$: Subject<boolean> = new Subject<boolean>();

  @ViewChild('remoteVideoContainerRef', { read: ViewContainerRef, static: true })
  public remoteVideoContainer!: ViewContainerRef;

  @ViewChild('myVideoContainerRef', { read: ViewContainerRef, static: true })
  private myVideoContainer!: ViewContainerRef;

  @ViewChild('whiteboardSharedRef', { read: ViewContainerRef, static: true })
  private whiteboardContainerRef!: ViewContainerRef;

  appModes = AppModesEnum;
  phoneStatuses = PhoneStatusEnum;

  constructor(
    private store: Store<AppState>,
    public mediaService: MediaStremService,
    private cdr: ChangeDetectorRef,
    public config: AppConfigService,
    public whiteboardService: WhiteboardService
  ) {}

  ngAfterViewInit(): void {}

  ngOnDestroy(): void {
    console.log(`*************** video.component remotevideo DESTROY **************`);
    this.mediaService.remoteVideoRefs.forEach(ref => ref.destroy());
    this.mediaService.remoteVideoRefs = [];
    this.destroy$.next(true);
    this.destroy$.complete();
    this.destroy$.unsubscribe();
  }

  ngOnInit() {
    setTimeout(() => {
      this.mediaService.init();
    }, 0);

    this.store.select(sessionSelector.selectPartecipants).pipe(
      takeUntil(this.destroy$)
    ).subscribe(data => {
      if (data) {
        const myInfo = data.find(z => z.partecipantId == this.config.partecipantId);
        const others = data.filter(z => z.partecipantId != this.config.partecipantId && z.active);
        this.addMyVideoContainer(myInfo);
        this.addVideoComponent(others);
      }
    });

    this.store.select(appSelector.guiLanguage).pipe(
      takeUntil(this.destroy$)
    ).subscribe(lang => {
      const parts = this.config.getPartecipantsStatus();
      this.mediaService.remoteVideoRefs.forEach(ref => {
        var name = I18Service.roleAndAlias(parts.find(p => p.partecipantId == ref.instance.id));
        if (ref.instance.id == this.config.partecipantId) {
          name = I18Service.get('Chat.Me');
        }
        ref.instance.name = name;
      });
    });
  }

  private async addMyVideoContainer(info: PartecipantInfo | undefined) {
    if (info) {
      const me = await this.addThumbVideo(info, this.myVideoContainer);
      me.instance.name = I18Service.get('Chat.Me');
    }
  }

  private async addVideoComponent(data: PartecipantInfo[]) {
    if (!data) return;

    data.forEach(p => {
      if (p.active) this.addThumbVideo(p, this.remoteVideoContainer);
    });
  }

  private async addThumbVideo(partecipant: PartecipantInfo, container: ViewContainerRef): Promise<ComponentRef<ThumbVideoComponent>> {
    const already = this.mediaService.remoteVideoRefs.find(z => z.instance.id == partecipant.partecipantId);
    if (already) {
      return already;
    }

    const { ThumbVideoComponent: ThumbVideoComponent } = await import('./thumb-video.component');
    const componentRef = container.createComponent(ThumbVideoComponent);

    componentRef.instance.id = partecipant.partecipantId;
    componentRef.instance.AS = this.AS;
    componentRef.instance.name = I18Service.roleAndAlias(partecipant);

    this.store.select(appSelector.selectOperatorZoomId).pipe(
      takeUntil(this.destroy$)
    ).subscribe(zoomId => {
      const isZoomed = (zoomId === partecipant.partecipantId);
      componentRef.instance.zoom = isZoomed;

      if (!isZoomed && componentRef.instance.zoomPanMode) {
        componentRef.instance.exitZoomPanMode();
      }
    });

    componentRef.instance.imageToWhiteboard.subscribe(canvasData => {
      if (canvasData) {
        this.config.setWhiteboardCanvasToEdit(canvasData, true); // fromVideo=true
        this.whiteboardService.open(canvasData);
        // unsavedChanges per video mode viene gestito dal flag openedFromVideo
        // in WhiteboardComponent.evaluateUnsavedChanges()

        const sub = this.whiteboardService.onClose$.subscribe(saved => {
          if (saved) {
            this.config.whiteboardReset();
          }
          sub.unsubscribe();
        });
      }
    });

    console.log(`mediaService - creating remotevideo for ${partecipant.alias} ${partToStr(partecipant)}`);

    this.mediaService.remoteVideoRefs.push(componentRef);

    this.cdr.detectChanges();
    return componentRef;
  }
}
